import { defineStore } from 'pinia';
import { useUI } from './ui';
import { useUsers } from './users';
import { methods } from '@/mixin';
import Node from './entity/Node';

import {
    API_ROUT_NODE_CREATE,
    API_ROUT_NODE_DOWNLOAD,
    API_ROUT_NODE_UPLOAD,
    API_ROUT_NODE_VIEW,
    API_ROUT_NODES,
    API_ROUT_NODE_UNARCHIVE,
    isMimeTypeArchive,
} from '@/constants';

import { IFilter, INodeCreate, INodeFromBack, INodesState, TFilterKeys, TKeySortBy } from '@/types';

const initFilter: IFilter = {
    name: new Set(),
    type: null,
    mime_type: new Set(),
    created_at: [0, 0],
    updated_at: [0, 0],
    deleted_at: [0, 0],
    user_creator: new Set(),
    user_updated: new Set(),
    user_deleted: new Set(),
    view: 'default',
};

const nodesState: INodesState = {
    nodes: [],
    selected: new Set(),
    nodeViewUrl: null,
    sortBy: {
        key: 'name',
        direction: true
    },
    filterBy: {...initFilter}
};

export const useNodes = defineStore('nodes', {

    state: () => (nodesState),

    getters: {

        all(): Node[]
        {
            return this.nodes;
        },

        existing(): Node[]
        {
            return this.nodes.filter((node: Node) => {
                return !node.deleted_at;
            });
        },

        deleted(): Node[]
        {
            return this.nodes.filter((node: Node) => {
                return node.deleted_at;
            });
        },

    },

    actions: {

        one(id: number | null): Node | undefined
        {
            if (!id) return undefined;
            return this.nodes.find((n: Node) => {
                return (n.id == id);
            });
        },

        isFolder(id: number): boolean
        {
            const node = this.one(id);
            if (node) {
                return node.type === 'folder';
            } else return false;
        },

        getMinMaxDate(type: 'created_at' | 'updated_at' | 'deleted_at', mode: 'max' |  'min') {
            const date = this.all.map((node: Node) => node[type]);//.filter((date: number | null) => date);
            const numbers: number[] = [];
            date.forEach((item: number | null) => {
                if (typeof item === 'number') numbers.push(item);
            });
            return Math[mode](...numbers);
        },

        getAll(mode: 'main' | 'deleted' = 'main', isSort: any = true, isFilter: any = true) {
            let nodes = mode === 'main' ? this.existing : this.deleted;
            if (isFilter) nodes = this.filterOn(nodes, mode, false);
            if (isSort) nodes = this.sort(nodes, mode);
            return nodes;
        },

        getChildrens(mode: 'main' | 'deleted' = 'main', id?: number | null, isSort: any = true, isFilter: any = true): Node[]
        {
            const parent = id;
            let nodes = mode === 'main' ? this.existing : this.deleted;
            if (isFilter) nodes = this.filterOn(nodes, mode);
            nodes = nodes.filter((node: Node) => {
                if (node.parent === parent) return true;
                else return false;
            });
            if (isSort) nodes = this.sort(nodes, mode);
            return this.sort(nodes, mode);
        },

        getBreadCrumbs(id?: number): (number | null)[]
        {
            let current = id ?? null;
            const result = [];

            do {
                result.push(current);
                if (current) {
                    const currNode = this.one(current);
                    if (currNode) {
                        current = currNode.parent;
                    }
                }
            } while (current);

            if (result[result.length - 1] !== null) result.push(null);

            result.reverse();

            return result;
        },

        getAncestors(node: Node, result?: number[]): any
        {
            if (!result) result = [];
            if (!node.parent) return result;
            const parentNode = this.one(node.parent);
            if (!parentNode) return result;
            result.push(node.parent);
            return this.getAncestors(parentNode, result);
        },

        isAllowEditByUser(node_id: number, user_id?: number, isCurrent?: boolean): boolean
        {
            const users = useUsers();
            
            if (!user_id && users.current) user_id = users.current.id;
            else return false;

            if (users.isAdmin(user_id)) return true;

            let result = false;
            const currentNode = this.one(node_id);

            if (!currentNode) return false;

            const initNodes = isCurrent ? [currentNode.id] : [];
            const ancestors = this.getAncestors(currentNode, initNodes);
            const userAccess = users.getAccess(user_id);

            userAccess.forEach(item => {
                if (ancestors.includes(item.node_id)) result = true;
            });

            return result;
        },

        sort(nodes?: Node[], mode: 'main' | 'deleted' = 'main') {
            if (!nodes) nodes = mode === 'main' ? this.existing : this.deleted;
            
            const users = useUsers();

            nodes.sort((a: Node, b: Node) => {

                const foldersPrefix = '!00000000000';
                let A: any = getItem(a);
                let B: any = getItem(b);

                function getItem(item: Node) {
                    const result: any = {...item};
                    const user_updated = users.one(item.user_updated ?? 0);
                    const user_deleted = users.one(item.user_deleted ?? 0);
                    result.user_updated = user_updated && user_updated.name ? user_updated.name : '';
                    result.user_deleted = user_deleted && user_deleted.name ? user_deleted.name : '';

                    if (result.type === 'folder') {
                        result.name = foldersPrefix + result.name;
                        result.user_updated = foldersPrefix + result.user_updated;
                        result.user_deleted = foldersPrefix + result.user_deleted;
                        result.updated_at = result.updated_at / 10000;
                        result.deleted_at = result.deleted_at / 10000;
                    }

                    return result;
                }

                switch (this.sortBy.key) {

                    case 'name':
                        if (this.sortBy.direction) return A.name > B.name ? 1 : -1;
                        else return A.name > B.name ? -1 : 1;
                    case 'user':
                        A = mode === 'main' ? A.user_updated : A.user_deleted;
                        B = mode === 'main' ? B.user_updated : B.user_deleted;
                        if (!A || !B) return A.id - B.id;
                        if (this.sortBy.direction) return A > B ? 1 : -1;
                        else return A > B ? -1 : 1;
                    case 'date':
                        A = mode === 'main' ? A.updated_at : (A.deleted_at ?? 0);
                        B = mode === 'main' ? B.updated_at : (B.deleted_at ?? 0);
                        if (this.sortBy.direction) return A - B;
                        else return B - A;
                    case 'size':
                        if (this.sortBy.direction) return A.size - B.size;
                        else return B.size - A.size;
                    default:
                        return A.id - B.id;
                }
            });
            return nodes;
        },

        getSortBy(key?: TKeySortBy) {
            if (key) {
                if (key === this.sortBy.key && this.sortBy.direction) return 'up';
                else if (key === this.sortBy.key && !this.sortBy.direction) return 'down';
                else return 'default';
            } else return this.sortBy;
        },

        setSortBy(key?: TKeySortBy, direction?: boolean) {
            if (key) this.sortBy.key = key;
            if (typeof direction === 'boolean') this.sortBy.direction = direction;
        },

        filterOn(nodes?: Node[], mode: 'main' | 'deleted' = 'main', isParentInclude: any = true): Node[]
        {
            if (!nodes) nodes = mode === 'main' ? this.existing : this.deleted;

            // console.log(this.filterBy, 'filterOn -> filterBy');

            return nodes;

            // nodes = nodes.filter((node: Node) => {

            //     let result = true;

            //     if (result && this.filterBy.name.size >= 1) {
            //         result = this.filterBy.name.has(node.name);
            //     }
            //     if (result && this.filterBy.type) {
            //         result = node.type === this.filterBy.type;
            //     }
            //     if (result && this.filterBy.mime_type.size >= 1) {
            //         result = this.filterBy.mime_type.has(node.mime_type);
            //     }
            //     if (result && (this.filterBy.created_at[0] || this.filterBy.created_at[1])) {
            //         if (this.filterBy.created_at[0] && this.filterBy.created_at[1]) {
            //             result = node.created_at > this.filterBy.created_at[0] && node.created_at < this.filterBy.created_at[1];
            //         } else {
            //             const date1 = Math.max(this.filterBy.created_at[0], this.filterBy.created_at[1]);
            //             const date2 = date1 + 24 * 60 * 60;
            //             result = node.created_at > date1 && node.created_at < date2;
            //         }
            //     }
            //     if (result && (this.filterBy.updated_at[0] || this.filterBy.updated_at[1])) {
            //         if (this.filterBy.updated_at[0] && this.filterBy.updated_at[1]) {
            //             result = node.updated_at > this.filterBy.updated_at[0] && node.updated_at < this.filterBy.updated_at[1];
            //         } else {
            //             const date1 = Math.max(this.filterBy.updated_at[0], this.filterBy.updated_at[1]);
            //             const date2 = date1 + 24 * 60 * 60;
            //             result = node.updated_at > date1 && node.updated_at < date2;
            //         }
            //     }
            //     if (result && node.deleted_at && (this.filterBy.deleted_at[0] || this.filterBy.deleted_at[1])) {
            //         if (this.filterBy.deleted_at[0] && this.filterBy.deleted_at[1]) {
            //             result = node.deleted_at > this.filterBy.deleted_at[0] && node.deleted_at < this.filterBy.deleted_at[1];
            //         } else {
            //             const date1 = Math.max(this.filterBy.deleted_at[0], this.filterBy.deleted_at[1]);
            //             const date2 = date1 + 24 * 60 * 60;
            //             result = node.deleted_at > date1 && node.deleted_at < date2;
            //         }
            //     }
            //     if (result && this.filterBy.user_creator.size >= 1) {
            //         result = this.filterBy.user_creator.has(node.user_creator);
            //     }
            //     if (result && this.filterBy.user_updated.size >= 1) {
            //         result = this.filterBy.user_updated.has(node.user_updated);
            //     }
            //     if (result && this.filterBy.user_deleted.size >= 1 && node.user_deleted) {
            //         result = this.filterBy.user_deleted.has(node.user_deleted);
            //     }

            //     return result;

            // });

            // if (isParentInclude) {
            //     const nodesId = new Set(nodes.map((node: Node) => node.id));
    
            //     nodes.forEach((node: Node) => {
            //         const ancestors = this.getAncestors(node);
            //         ancestors.forEach((id: number) => nodesId.add(id));
            //     });
    
            //     nodes = this.all.filter((node: Node) => {
            //         if (nodesId.has(node.id)) return true;
            //         else return false;
            //     });
            // }

            // return nodes;
        },

        isFilterOn() {
            return !(
                this.filterBy.name.size === 0 &&
                this.filterBy.type === null &&
                this.filterBy.mime_type.size === 0 &&
                (!this.filterBy.created_at[0] && !this.filterBy.created_at[1])  &&
                (!this.filterBy.updated_at[0] && !this.filterBy.updated_at[1])  &&
                (!this.filterBy.deleted_at[0] && !this.filterBy.deleted_at[1])  &&
                this.filterBy.user_creator.size === 0 &&
                this.filterBy.user_updated.size === 0 &&
                this.filterBy.user_deleted.size === 0 &&
                this.filterBy.view === 'default'
            );
        },

        addFilter(type: TFilterKeys, data: string | number, index?: number) {

            if (type === 'type' && (data === 'file' || data === 'folder')) {
                this.filterBy[type] = data;
            }

            if ((type === 'mime_type' || type === 'name') && typeof data === 'string') {
                this.filterBy[type].add(data);
            } else if (
                (
                    type === 'user_creator' ||
                    type === 'user_updated' ||
                    type === 'user_deleted'
                ) && typeof data === 'number'
            ) {
                this.filterBy[type].add(data);
            } else if (
                (
                    type === 'created_at' ||
                    type === 'updated_at' ||
                    type === 'deleted_at'
                ) && typeof data === 'number' && typeof index === 'number'
            ) {
                this.filterBy[type][index] = data;
            }
        },

        removeFilter(type: TFilterKeys | 'all', data?: string | number, index?: number) {
            if (type !== 'all' && type !== 'type' && !type.endsWith('_at') && data === undefined) return;

            if (type === 'type') {
                this.filterBy.type = null;
                return true;
            }

            if (type === 'all') {
                this.filterBy = {...initFilter};
                return true;
            }

            if (
                (
                    type === 'user_creator' ||
                    type === 'user_updated' ||
                    type === 'user_deleted'
                ) && typeof data === 'number'
            ) {
                return this.filterBy[type].delete(data);
            }
            
            if (
                (
                    type === 'created_at' ||
                    type === 'updated_at' ||
                    type === 'deleted_at'
                ) && typeof index === 'number'
            ) {
                this.filterBy[type][index] = 0;
            }

            if (      
                (            
                    type === 'name' ||
                    type === 'mime_type'
                ) && typeof data === 'string'
            ) {
                return this.filterBy[type].delete(data);
            }

        },

        setFilterView(payload: 'default' | 'all') {
            this.filterBy.view = payload;
        },

        async fetch(folder: number | null | 'back' = null, isTrash = false) {

            const currentRoute: any = this.$router.currentRoute;

            const filter = {
                ...this.filterBy,
                name: Array.from(this.filterBy.name),
                mime_type: Array.from(this.filterBy.mime_type),
                user_creator: Array.from(this.filterBy.user_creator),
                user_updated: Array.from(this.filterBy.user_updated),
                user_deleted: Array.from(this.filterBy.user_deleted),
            };

            this.nodes = [];

            if (folder === 'back') {
                folder = (currentRoute.query && currentRoute.query.folder) ? currentRoute.query.folder : null;
            }

            // const nodes = await methods.fetch(`${API_ROUT_NODES}/?folder=${folder}&trash=${isTrash === true ? 1 : 0}`).get();
            const nodes = await methods.fetch(API_ROUT_NODES).post({
                folder,
                trash: isTrash ? 1 : 0,
                filter,
            }, true);
            
            try {
                if (Array.isArray(nodes)) {
                    nodes.forEach(item => {
                        this.push(item);
                    });
                }
            } catch(err: any) {
                useUI().setNotification('error', 'Помилка: ' + err.message, 'fetchNodeError');
            }
        },

        async create(node: INodeCreate, isUploadFile?: boolean) {

            const ui = useUI();

            ui.ringLoader = true;

            const url = isUploadFile ? API_ROUT_NODE_UPLOAD : API_ROUT_NODE_CREATE;
            const postData = isUploadFile ? node.data : node;
            const response: INodeFromBack = await methods.fetch(url).post(postData);

            if (response && response.id) {
                this.push(response);
            } else {
                ui.setNotification(
                    'error',
                    `Не вдалось створити об'єкт "${node.name}".`,
                    `createNodeError-${node.name}`
                );
            }

            ui.ringLoader = false;

        },

        async update(node: Node) {

            const ui = useUI();

            ui.ringLoader = true;

            const response = await methods.fetch(`${API_ROUT_NODES}/${node.id}`).patch({
                name: node.name,
                parent: node.parent,
                color: node.color,
            });

            ui.ringLoader = false;

            return response;
        },

        async trash(node: Node) {

            const ui = useUI();

            ui.ringLoader = true;

            const response = await methods.fetch(`${API_ROUT_NODES}/trash/${node.id}`).patch();

            ui.ringLoader = false;

            return response;
        },

        async delete(node: Node) {

            const ui = useUI();

            ui.ringLoader = true;

            const response = await methods.fetch(`${API_ROUT_NODES}/${node.id}`).delete();

            ui.ringLoader = false;

            return response;
        },

        async download(node: Node | Node[]) {

            const ui = useUI();

            ui.ringLoader = true;

            if (!Array.isArray(node)) node = [node];
            const ids = node.map(item => item.id);
            const response = await methods.fetch(`${API_ROUT_NODE_DOWNLOAD}?ids=${ids.join(',')}`).get();

            ui.ringLoader = false;

            return response;
        },

        async view(node: Node) {

            const ui = useUI();

            ui.ringLoader = true;

            const response = await methods.fetch(`${API_ROUT_NODE_VIEW}/${node.id}`).get();

            ui.ringLoader = false;

            return response;
        },

        async unArchive(node: Node) {

            if (!isMimeTypeArchive(node.mime_type)) {
                console.warn(node, 'Is not archive! (unArchive func)');
                return;
            }

            const ui = useUI();

            ui.ringLoader = true;

            const response = await methods.fetch(`${API_ROUT_NODE_UNARCHIVE}/${node.id}`).patch();

            ui.ringLoader = false;

            return response;
        },

        push(value: INodeFromBack | INodeFromBack[]) {
            if (!Array.isArray(value)) value = [value];
            value.forEach((item: INodeFromBack) => {
                if (!this.nodes.some(u => u.id == item.id)) this.nodes.push(new Node(item));
            });
        },

        replace(value: INodeFromBack | INodeFromBack[]) {
            if (!Array.isArray(value)) value = [value];
            this.nodes = this.nodes.map((node: Node) => {
                if (!Array.isArray(value)) return node;
                const newNode = value.find((item: Node) => item.id === node.id);
                if (newNode) return newNode;
                else return node;
            });
        },

        remove(value: INodeFromBack | INodeFromBack[]) {
            if (!Array.isArray(value)) value = [value];
            this.nodes = this.nodes.filter((node: Node) => {
                if (!Array.isArray(value)) return;
                const deletedNode = value.find((item: Node) => item.id === node.id);
                if (deletedNode) return false;
                else return true;
            });
        },

    },
});
